"use client";

import { useEffect, useRef, useState } from "react";
import { useCanAnimate } from "@/hooks/useCanAnimate";
import { useIsMobile } from "@/hooks/useIsMobile";

// Convert HSL hue to RGB (with optional lightness for light mode)
function hueToRgb(hue: number, lightness: number = 0.5): { r: number; g: number; b: number } {
  const s = 1;
  const l = lightness;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + hue / 30) % 12;
    return l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
  };
  return {
    r: Math.round(f(0) * 255),
    g: Math.round(f(8) * 255),
    b: Math.round(f(4) * 255),
  };
}

// Convert HSL hue to hex (with optional lightness for light mode)
function hueToHex(hue: number, lightness: number = 0.5): string {
  const rgb = hueToRgb(hue, lightness);
  return `#${rgb.r.toString(16).padStart(2, '0')}${rgb.g.toString(16).padStart(2, '0')}${rgb.b.toString(16).padStart(2, '0')}`;
}

// Parse hex color to RGB
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) }
    : { r: 255, g: 255, b: 255 };
}

interface MiniCubeProps {
  size?: number;
  hue?: number;
  color?: string; // Override hue with direct color (e.g., "white", "#ffffff")
  isLightMode?: boolean;
}

export function MiniCube({ size = 120, hue = 50, color, isLightMode = false }: MiniCubeProps) {
  const [mousePos, setMousePos] = useState({ x: 0.5, y: 0.5 });
  const [time, setTime] = useState(0);
  const [orbitAngle, setOrbitAngle] = useState(0);
  const [isPulsing, setIsPulsing] = useState(false);
  const [currentHue, setCurrentHue] = useState(hue);
  const [isVisible, setIsVisible] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const isPulsingRef = useRef(false);
  const orbitSpeedRef = useRef(1);
  const canAnimate = useCanAnimate();
  const isMobile = useIsMobile();

  // Track visibility to pause animations when offscreen
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0, rootMargin: "50px 0px 50px 0px" } // Small buffer
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Smoothly transition hue (only when visible)
  useEffect(() => {
    if (!isVisible) return;
    let animationId: number;
    const animate = () => {
      setCurrentHue((prev) => {
        const diff = hue - prev;
        if (Math.abs(diff) < 0.5) return hue;
        return prev + diff * 0.08;
      });
      animationId = requestAnimationFrame(animate);
    };
    animate();
    return () => cancelAnimationFrame(animationId);
  }, [hue, isVisible]);

  // Use CSS variables for smooth theme transitions
  // Falls back to computed colors only if needed for non-themed usage
  const useCssVars = !color || color === "var(--theme-fg)";
  const lightness = isLightMode ? 0.3 : 0.5; // Darker in light mode

  // RGB string for use in rgba() - either CSS var or "r, g, b" format
  const rgbString = useCssVars
    ? "var(--theme-fg-rgb)"
    : (() => {
        const rgb = color ? hexToRgb(color) : hueToRgb(currentHue, lightness);
        return `${rgb.r}, ${rgb.g}, ${rgb.b}`;
      })();

  // Hex color for solid fills
  const hexColor = useCssVars ? "var(--theme-fg)" : (color || hueToHex(currentHue, lightness));

  // Transition duration for theme changes
  const themeTransition = "var(--theme-transition) var(--theme-transition-ease)";

  const handleClick = () => {
    setIsPulsing(true);
    isPulsingRef.current = true;
    setTimeout(() => {
      setIsPulsing(false);
      isPulsingRef.current = false;
    }, 400);
  };

  // Track global mouse position for scroll updates
  const clientMousePos = useRef({ x: -1000, y: -1000 });

  // Recalculate mouse position relative to cube
  const updateMousePosFromClient = () => {
    if (containerRef.current && clientMousePos.current.x >= 0) {
      const rect = containerRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const dist = Math.sqrt(
        Math.pow(clientMousePos.current.x - centerX, 2) + Math.pow(clientMousePos.current.y - centerY, 2)
      );
      if (dist < 300) {
        setMousePos({
          x: (clientMousePos.current.x - rect.left) / rect.width,
          y: (clientMousePos.current.y - rect.top) / rect.height,
        });
      }
    }
  };

  useEffect(() => {
    // Skip all mouse/animation tracking when motion is disabled
    if (!canAnimate) return;

    const handleMouseMove = (e: MouseEvent) => {
      clientMousePos.current = { x: e.clientX, y: e.clientY };
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        // Respond to mouse within 300px of cube
        const dist = Math.sqrt(
          Math.pow(e.clientX - centerX, 2) + Math.pow(e.clientY - centerY, 2)
        );
        if (dist < 300) {
          setMousePos({
            x: (e.clientX - rect.left) / rect.width,
            y: (e.clientY - rect.top) / rect.height,
          });
        }
      }
    };

    const handleScroll = () => {
      updateMousePosFromClient();
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Only run JS animations if visible AND not mobile
    // Mobile uses CSS animations instead for performance
    let interval: NodeJS.Timeout | undefined;
    if (isVisible && !isMobile) {
      interval = setInterval(() => {
        setTime((t) => t + 0.02);
        // Smoothly interpolate orbit speed
        const targetSpeed = isPulsingRef.current ? 16 : 1;
        orbitSpeedRef.current += (targetSpeed - orbitSpeedRef.current) * 0.1;
        setOrbitAngle((a) => a + orbitSpeedRef.current);
      }, 16);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("scroll", handleScroll);
      if (interval) clearInterval(interval);
    };
  }, [canAnimate, isVisible, isMobile]);

  // Static positions for low-power mode, animated otherwise
  // Use nice default angle when motion off or on mobile (no hover tracking)
  const staticRotation = (isMobile || !canAnimate) ? { x: -15, y: 25 } : { x: 0, y: 0 };
  const rotateX = !canAnimate
    ? staticRotation.x
    : -(mousePos.y - 0.5) * 20 + Math.sin(time) * 2 + staticRotation.x;
  const rotateY = !canAnimate
    ? staticRotation.y
    : (mousePos.x - 0.5) * 20 + Math.cos(time * 0.7) * 2 + staticRotation.y;

  const faces = [
    { ry: 0, rx: 0 },
    { ry: 180, rx: 0 },
    { ry: 90, rx: 0 },
    { ry: -90, rx: 0 },
    { ry: 0, rx: 90 },
    { ry: 0, rx: -90 },
  ];

  const innerFaces = [
    { ry: 0, rx: 0, opacity: 0.15 },
    { ry: 180, rx: 0, opacity: 0.08 },
    { ry: 90, rx: 0, opacity: 0.1 },
    { ry: -90, rx: 0, opacity: 0.1 },
    { ry: 0, rx: 90, opacity: 0.08 },
    { ry: 0, rx: -90, opacity: 0.08 },
  ];

  const outerSize = size * 0.65;
  const innerSize = size * 0.36;
  const outerTranslateZ = size * 0.325;
  const innerTranslateZ = size * 0.18;
  const orbSize = size * 0.12;

  return (
    <div
      ref={containerRef}
      onClick={handleClick}
      className="relative cursor-pointer"
      style={{
        width: size,
        height: size,
        perspective: "800px",
        // Hide 3D rendering when off-screen to reduce GPU load
        visibility: isVisible ? "visible" : "hidden",
        contentVisibility: isVisible ? "visible" : "hidden",
        willChange: isVisible ? "transform" : "auto",
      }}
    >
      <div
        className="absolute inset-0"
        style={{
          transition: "transform var(--duration-fast) ease-out",
          transform: `rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
          transformStyle: "preserve-3d",
        }}
      >
        {/* Outer wireframe */}
        {faces.map((face, i) => (
          <div
            key={`outer-${i}`}
            className="absolute rounded-lg"
            style={{
              width: outerSize,
              height: outerSize,
              left: "50%",
              top: "50%",
              transform: `translate(-50%, -50%) rotateX(${face.rx}deg) rotateY(${face.ry}deg) translateZ(${outerTranslateZ}px)`,
              border: `1px solid rgba(${rgbString}, 0.3)`,
              boxShadow: `inset 0 0 20px rgba(${rgbString}, 0.05)`,
              transition: `border-color ${themeTransition}, box-shadow ${themeTransition}`,
            }}
          />
        ))}

        {/* Inner wireframe faces */}
        {innerFaces.map((face, i) => (
          <div
            key={`inner-${i}`}
            className="absolute rounded-lg"
            style={{
              width: innerSize,
              height: innerSize,
              left: "50%",
              top: "50%",
              transform: `translate(-50%, -50%) rotateX(${face.rx}deg) rotateY(${face.ry}deg) translateZ(${innerTranslateZ}px)`,
              background: `rgba(${rgbString}, ${face.opacity * 0.35})`,
              border: "1px solid rgba(255,255,255,0.08)",
              transition: `background ${themeTransition}`,
            }}
          />
        ))}

        {/* Core orb */}
        <div
          className="absolute left-1/2 top-1/2 rounded-full"
          style={{
            width: orbSize * 1.4,
            height: orbSize * 1.4,
            transform: `translate(-50%, -50%) translateZ(0) scale(${isPulsing ? 1.8 : 1})`,
            transition: isPulsing
              ? `transform var(--duration-fast) ease-out, filter var(--duration-fast) ease-out, background ${themeTransition}, box-shadow ${themeTransition}`
              : `transform var(--duration-normal) ease-out, filter var(--duration-normal) ease-out, background ${themeTransition}, box-shadow ${themeTransition}`,
            background:
              `radial-gradient(circle, rgba(${rgbString},1) 0%, rgba(${rgbString},0.6) 30%, rgba(${rgbString},0.2) 55%, rgba(${rgbString},0) 70%)`,
            filter: isPulsing ? "blur(6px)" : "blur(3px)",
            boxShadow: isPulsing
              ? `0 0 50px rgba(${rgbString},1), 0 0 100px rgba(${rgbString},0.6)`
              : `0 0 30px rgba(${rgbString},0.8), 0 0 60px rgba(${rgbString},0.4)`,
          }}
        />

        {/* Orbiting rings - nucleus effect (hidden when motion off) */}
        {/* Mobile: CSS animation, Desktop: JS-driven orbitAngle */}
        {canAnimate && [0, 72, 144, 216, 288].map((offset, i) => (
          <div
            key={i}
            className={`absolute left-1/2 top-1/2 rounded-full ${isMobile ? "minicube-orbit" : ""}`}
            style={{
              width: size * 0.5,
              height: size * 0.5,
              transform: isMobile
                ? `translate(-50%, -50%) rotateX(70deg)`
                : `translate(-50%, -50%) rotateX(70deg) rotateZ(${orbitAngle + offset}deg)`,
              border: `1px solid rgba(${rgbString}, 0.4)`,
              transition: `border-color ${themeTransition}`,
              animationDelay: isMobile ? `${-offset / 60}s` : undefined,
              animationPlayState: isVisible ? "running" : "paused",
            }}
          >
            <div
              className="absolute -top-[3px] left-1/2 -ml-[3px] w-[6px] h-[6px] rounded-full"
              style={{
                backgroundColor: hexColor,
                boxShadow: `0 0 6px ${hexColor}, 0 0 12px rgba(${rgbString},0.5)`,
                transition: `background-color ${themeTransition}, box-shadow ${themeTransition}`,
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
